import React, { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { TabNavigation } from "@beleef/ui";
import { FiAlertCircle } from "react-icons/fi";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import { useGetJurisdictionsQuery } from "apis/division";
import { useGetNetworksQuery } from "apis/network";
import {
  CONTROL_LEVELS,
  DEFAULT_CONTROL_LEVEL,
  GROUP_OVERRIDE_SETTINGS,
  STATUS_SELECT_OPTIONS,
  getControlLevels,
  getFieldError,
  inputClassName,
  optionalEmail,
} from "utils/network";

// Each tab owns a slice of the form. `fields` are the top-level keys whose
// errors belong to that tab, so a failure in a tab you are not looking at still
// announces itself on the tab strip.
const TABS = [
  {
    value: "identity",
    label: "Identity",
    fields: ["networkId", "name", "status"],
  },
  { value: "jurisdictions", label: "Jurisdictions", fields: ["governs"] },
  { value: "emails", label: "Emails", fields: ["operationalEmails"] },
  { value: "overrides", label: "Overrides", fields: ["controlledBy"] },
];

const Field = ({ label, error, hint, children }) => (
  <div className="space-y-2">
    <label className="form-label">{label}</label>
    {children}
    {hint ? <p className="text-xs text-darkgray">{hint}</p> : null}
    {error ? <p className="form-error-message">{error}</p> : null}
  </div>
);

const InputField = ({ label, error, hint, inputProps }) => (
  <Field label={label} error={error} hint={hint}>
    <input {...inputProps} className={inputClassName(error)} />
  </Field>
);

// Country-level codes (AU) are rule containers, not places a listing sits, so
// they are not offered here.
const isPlaceable = (jurisdiction) => Boolean(jurisdiction.parent);

export default function DivisionEdit({ initialData = {}, onSubmit }) {
  const [activeTab, setActiveTab] = useState("identity");
  const [jurisdictionFilter, setJurisdictionFilter] = useState("");
  const { data: networks } = useGetNetworksQuery();
  const { data: jurisdictions } = useGetJurisdictionsQuery();

  const networkOptions = useMemo(
    () => networks.map((network) => ({ value: network._id, label: network.name })),
    [networks],
  );

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      networkId: initialData.networkId?._id || initialData.networkId || "",
      name: initialData.name || "",
      status: initialData.status || "active",
      governs: initialData.governs || [],
      operationalEmails: {
        settlements: initialData.operationalEmails?.settlements || "",
        concierge: initialData.operationalEmails?.concierge || "",
        accounts: initialData.operationalEmails?.accounts || "",
        admin: initialData.operationalEmails?.admin || "",
        keyEvents: initialData.operationalEmails?.keyEvents || "",
      },
      controlledBy: getControlLevels(initialData),
    },
  });

  const tabs = useMemo(
    () =>
      TABS.map((tab) => ({
        value: tab.value,
        label: tab.label,
        icon: tab.fields.some((field) => errors[field])
          ? FiAlertCircle
          : undefined,
      })),
    [errors],
  );

  const submitForm = (data) =>
    onSubmit({
      ...data,
      // Only entries that actually narrow something. An unlisted path already
      // resolves to the network, so "network" rows would be noise.
      controlledBy: GROUP_OVERRIDE_SETTINGS.filter(
        (setting) =>
          data.controlledBy?.[setting.key] &&
          data.controlledBy[setting.key] !== DEFAULT_CONTROL_LEVEL,
      ).map((setting) => ({
        path: setting.key,
        level: data.controlledBy[setting.key],
      })),
    });

  // Grouped by country, because a division can cover several jurisdictions and
  // the list only grows — the US alone is 50 states, each its own commission.
  const jurisdictionGroups = useMemo(() => {
    const byCode = new Map(jurisdictions.map((j) => [j.code, j]));
    const groups = new Map();
    for (const jurisdiction of jurisdictions) {
      if (!isPlaceable(jurisdiction)) continue;
      const parent = jurisdiction.parent;
      if (!groups.has(parent)) {
        groups.set(parent, {
          code: parent,
          name: byCode.get(parent)?.name || parent,
          items: [],
        });
      }
      groups.get(parent).items.push(jurisdiction);
    }
    return [...groups.values()];
  }, [jurisdictions]);

  const visibleGroups = useMemo(() => {
    const term = jurisdictionFilter.trim().toLowerCase();
    if (!term) return jurisdictionGroups;
    return jurisdictionGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (j) =>
            j.code.toLowerCase().includes(term) ||
            j.name.toLowerCase().includes(term),
        ),
      }))
      .filter((group) => group.items.length);
  }, [jurisdictionGroups, jurisdictionFilter]);

  const placeableCount = jurisdictionGroups.reduce(
    (total, group) => total + group.items.length,
    0,
  );

  const isExisting = Boolean(initialData._id);

  return (
    <div className="flex flex-col gap-5">
      {/* Outside the form on purpose: @beleef/ui TabNavigation renders its tabs
          as <button> with no type, which defaults to submit inside a form — so
          switching tabs would submit and close the modal. */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <form onSubmit={handleSubmit(submitForm)} className="flex flex-col gap-5">
        {activeTab === "identity" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field
              label="Network *"
              error={getFieldError(errors, "networkId")}
              hint={
                isExisting
                  ? "Frozen after creation — moving a division would strand its groups."
                  : undefined
              }
            >
              <Controller
                control={control}
                name="networkId"
                rules={{ required: "Network is required" }}
                render={({ field }) => (
                  <SearchSelect
                    options={networkOptions}
                    value={
                      networkOptions.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    onChange={(option) => field.onChange(option?.value || "")}
                    placeholder="Select network..."
                    disabled={isSubmitting || isExisting}
                  />
                )}
              />
            </Field>

            <InputField
              label="Name *"
              error={getFieldError(errors, "name")}
              hint="The head office's name — Stone NSW, Stone Queensland."
              inputProps={{
                type: "text",
                placeholder: "Stone NSW",
                disabled: isSubmitting,
                ...register("name", { required: "Name is required" }),
              }}
            />

            <Field label="Status">
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <SearchSelect
                    options={STATUS_SELECT_OPTIONS}
                    value={
                      STATUS_SELECT_OPTIONS.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    onChange={(option) => field.onChange(option?.value || "")}
                    placeholder="Select status..."
                    searchable={false}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Field>
          </div>
        ) : null}

        {activeTab === "jurisdictions" ? (
          <Controller
            control={control}
            name="governs"
            render={({ field }) => (
              <div className="flex flex-col gap-4">
                <p className="text-xs text-darkgray">
                  A listing takes its rules from whichever division governs the
                  jurisdiction of its own address — not from the division its
                  group sits under. One jurisdiction may be governed by only one
                  division per network.
                </p>
                {!field.value?.length ? (
                  <p className="text-xs text-red-500">
                    A division governing nothing is inert — no listing will
                    resolve through it.
                  </p>
                ) : null}

                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={jurisdictionFilter}
                    onChange={(event) =>
                      setJurisdictionFilter(event.target.value)
                    }
                    placeholder="Filter by code or name..."
                    disabled={isSubmitting}
                    className={inputClassName()}
                  />
                  <span className="text-xs text-darkgray whitespace-nowrap tabular-nums">
                    {(field.value || []).length} of {placeableCount} selected
                  </span>
                </div>

                <div className="border border-mediumgray rounded-md max-h-80 overflow-y-auto">
                  {visibleGroups.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-darkgray text-center">
                      No jurisdiction matches "{jurisdictionFilter}".
                    </p>
                  ) : (
                    visibleGroups.map((group) => {
                      const codes = group.items.map((item) => item.code);
                      const allSelected = codes.every((code) =>
                        (field.value || []).includes(code),
                      );
                      return (
                        <div key={group.code}>
                          {/* Sticky so the country stays visible while scrolling
                              a long list — the US alone will fill the box. */}
                          <div className="sticky top-0 z-10 flex items-center gap-3 bg-lightergray px-4 py-2 border-t border-mediumgray first:border-t-0">
                            <span className="text-[11px] uppercase tracking-[0.06em] font-semibold text-darkergray">
                              {group.name}
                            </span>
                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() =>
                                field.onChange(
                                  allSelected
                                    ? (field.value || []).filter(
                                        (code) => !codes.includes(code),
                                      )
                                    : [
                                        ...new Set([
                                          ...(field.value || []),
                                          ...codes,
                                        ]),
                                      ],
                                )
                              }
                              className="ml-auto text-xs text-black underline underline-offset-2 disabled:opacity-50"
                            >
                              {allSelected ? "Clear all" : "Select all"}
                            </button>
                          </div>

                          {group.items.map((jurisdiction) => {
                            const checked = (field.value || []).includes(
                              jurisdiction.code,
                            );
                            return (
                              <label
                                key={jurisdiction.code}
                                className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-lightergray border-t border-mediumgray"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={isSubmitting}
                                  onChange={(event) =>
                                    field.onChange(
                                      event.target.checked
                                        ? [
                                            ...(field.value || []),
                                            jurisdiction.code,
                                          ]
                                        : (field.value || []).filter(
                                            (code) => code !== jurisdiction.code,
                                          ),
                                    )
                                  }
                                />
                                <span className="font-mono text-xs w-20 flex-shrink-0">
                                  {jurisdiction.code}
                                </span>
                                <span className="text-sm min-w-0">
                                  {jurisdiction.name}
                                </span>
                                {/* The tell-tale that two states differ —
                                    Brisbane has no daylight saving. */}
                                <span className="ml-auto text-xs text-darkgray flex-shrink-0">
                                  {jurisdiction.timezone}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          />
        ) : null}

        {activeTab === "emails" ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-darkgray">
              Left blank, a group inherits the network's. A missing address is
              not an error anywhere downstream — the message is simply never
              sent.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                ["settlements", "Settlements"],
                ["concierge", "Concierge"],
                ["accounts", "Accounts"],
                ["admin", "Admin"],
                ["keyEvents", "Key events"],
              ].map(([key, label]) => (
                <InputField
                  key={key}
                  label={label}
                  error={getFieldError(errors, `operationalEmails.${key}`)}
                  inputProps={{
                    type: "text",
                    placeholder: `${key}@example.com`,
                    disabled: isSubmitting,
                    ...register(`operationalEmails.${key}`, {
                      validate: optionalEmail(label),
                    }),
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}

        {activeTab === "overrides" ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-darkgray">
              Narrows what the network allows. A division can tighten a setting
              for its own groups, but can never open one up beyond the network's
              value. Leave at Network only to inherit.
            </p>
            <div className="border border-mediumgray rounded-md overflow-hidden">
              {GROUP_OVERRIDE_SETTINGS.map((setting) => (
                <Controller
                  key={setting.key}
                  control={control}
                  name={`controlledBy.${setting.key}`}
                  render={({ field }) => (
                    <div className="flex items-start gap-3.5 px-4 py-3 border-t border-mediumgray first:border-t-0">
                      <div className="min-w-0">
                        <div className="font-medium text-black">
                          {setting.name}
                        </div>
                        <div className="text-xs text-darkgray mt-0.5">
                          {setting.description}
                        </div>
                      </div>
                      <div className="ml-auto flex-shrink-0 w-44">
                        <SearchSelect
                          options={CONTROL_LEVELS}
                          value={
                            CONTROL_LEVELS.find(
                              (level) =>
                                level.value ===
                                (field.value || DEFAULT_CONTROL_LEVEL),
                            ) || null
                          }
                          onChange={(option) =>
                            field.onChange(option?.value || DEFAULT_CONTROL_LEVEL)
                          }
                          placeholder="Network only"
                          searchable={false}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  )}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-mediumgray pt-4">
          {Object.keys(errors).length > 0 ? (
            <span className="text-xs text-red-500 mr-auto">
              Some fields need attention — check the flagged tabs.
            </span>
          ) : null}
          <Button
            type="submit"
            className="black-button"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isExisting ? "Update Division" : "Create Division"}
          </Button>
        </div>
      </form>
    </div>
  );
}
